import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Building2, Store, Users, Shield, Globe, ExternalLink, LogIn, Send, 
  Loader2, CheckCircle, ChevronDown, Sparkles, ArrowRight, Play,
  Gamepad2, FileText, Coins, Dumbbell
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Logo URLs
const LOGO_TRIANGLE = '/brand/ast-logo-asb606.png';
const LOGO_FULL = '/brand/ast-logo-asb606.png';
// المثلث فقط للخلفية (بدون النص)
const TRIANGLE_ONLY = '/brand/ast-logo-asb606.png';

// Animation Variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 }
  }
};

// Floating Particles Component
const FloatingParticles = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(20)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute w-1 h-1 bg-[#d4af37]/30 rounded-full"
        initial={{ 
          x: Math.random() * 100 + '%', 
          y: Math.random() * 100 + '%',
          opacity: 0 
        }}
        animate={{ 
          y: [null, '-100vh'],
          opacity: [0, 1, 0]
        }}
        transition={{ 
          duration: Math.random() * 10 + 10,
          repeat: Infinity,
          delay: Math.random() * 5
        }}
      />
    ))}
  </div>
);

// Contact Form Component
const ContactForm = ({ language }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    title: '',
    subject: ''
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.title || !formData.subject) return;
    
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact-requests`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        title: formData.title,
        subject: formData.subject,
        platform: 'website'
      });
      setSent(true);
      setFormData({ name: '', email: '', phone: '', title: '', subject: '' });
    } catch (err) {
      console.error('Error sending contact request:', err);
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white/[0.02] backdrop-blur-xl rounded-3xl border border-white/10 p-12 text-center"
      >
        <motion.div 
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", duration: 0.6 }}
          className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6"
        >
          <CheckCircle className="h-10 w-10 text-emerald-400" />
        </motion.div>
        <h3 className="text-2xl font-bold text-white mb-3">
          {language === 'ar' ? 'تم إرسال رسالتك بنجاح!' : 'Message Sent Successfully!'}
        </h3>
        <p className="text-[#A1A1AA] mb-6">
          {language === 'ar' ? 'سنتواصل معك في أقرب وقت ممكن.' : 'We\'ll get back to you as soon as possible.'}
        </p>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setSent(false)}
          className="px-8 py-3 bg-[#d4af37] text-black font-bold rounded-xl hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-all"
        >
          {language === 'ar' ? 'إرسال رسالة أخرى' : 'Send Another Message'}
        </motion.button>
      </motion.div>
    );
  }

  return (
    <motion.form 
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      onSubmit={handleSubmit} 
      className="bg-white/[0.02] backdrop-blur-xl rounded-3xl border border-white/10 p-8 space-y-5"
    >
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className="block text-white font-medium mb-2 text-sm">
            {language === 'ar' ? 'الاسم' : 'Name'} *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder={language === 'ar' ? 'أدخل اسمك الكامل' : 'Enter your full name'}
            className="w-full px-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-[#71717A] focus:border-[#d4af37]/50 focus:bg-white/[0.08] focus:outline-none transition-all"
            required
          />
        </div>
        <div>
          <label className="block text-white font-medium mb-2 text-sm">
            {language === 'ar' ? 'البريد الإلكتروني' : 'Email'} *
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            placeholder="example@email.com"
            className="w-full px-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-[#71717A] focus:border-[#d4af37]/50 focus:bg-white/[0.08] focus:outline-none transition-all"
            dir="ltr"
            required
          />
        </div>
      </div>
      <div>
        <label className="block text-white font-medium mb-2 text-sm">
          {language === 'ar' ? 'عنوان الموضوع' : 'Subject Title'} *
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          placeholder={language === 'ar' ? 'اكتب عنوان الموضوع' : 'Enter subject title'}
          className="w-full px-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-[#71717A] focus:border-[#d4af37]/50 focus:bg-white/[0.08] focus:outline-none transition-all"
          required
        />
      </div>
      <div>
        <label className="block text-white font-medium mb-2 text-sm">
          {language === 'ar' ? 'الرسالة' : 'Message'} *
        </label>
        <textarea
          value={formData.subject}
          onChange={(e) => {
            if (e.target.value.length <= 1000) {
              setFormData(prev => ({ ...prev, subject: e.target.value }));
            }
          }}
          placeholder={language === 'ar' ? 'اكتب رسالتك هنا...' : 'Write your message here...'}
          rows={5}
          className="w-full px-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-[#71717A] focus:border-[#d4af37]/50 focus:bg-white/[0.08] focus:outline-none transition-all resize-none"
          required
        />
        <p className="text-xs text-[#71717A] mt-2 text-left" dir="ltr">
          {formData.subject.length}/1000
        </p>
      </div>
      <motion.button
        whileHover={{ scale: 1.02, boxShadow: "0 0 30px rgba(212,175,55,0.3)" }}
        whileTap={{ scale: 0.98 }}
        type="submit"
        disabled={sending || !formData.name || !formData.email || !formData.title || !formData.subject}
        className="w-full py-4 bg-gradient-to-r from-[#d4af37] to-[#c9a227] text-black font-bold rounded-xl hover:shadow-[0_0_40px_rgba(212,175,55,0.4)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {sending ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            {language === 'ar' ? 'جاري الإرسال...' : 'Sending...'}
          </>
        ) : (
          <>
            <Send className="h-5 w-5" />
            {language === 'ar' ? 'إرسال الرسالة' : 'Send Message'}
          </>
        )}
      </motion.button>
    </motion.form>
  );
};

// Stats Counter Component
const StatCounter = ({ value, label, delay = 0 }) => {
  const [count, setCount] = useState(0);
  const numericValue = parseInt(value.replace(/\D/g, '')) || 0;
  
  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const stepValue = numericValue / steps;
    let current = 0;
    
    const timer = setTimeout(() => {
      const interval = setInterval(() => {
        current += stepValue;
        if (current >= numericValue) {
          setCount(numericValue);
          clearInterval(interval);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);
      
      return () => clearInterval(interval);
    }, delay);
    
    return () => clearTimeout(timer);
  }, [numericValue, delay]);

  return (
    <div className="text-center">
      <motion.p 
        initial={{ opacity: 0, scale: 0.5 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#d4af37] via-[#f3e5ab] to-[#c9a227] text-transparent bg-clip-text"
      >
        {count}{value.includes('+') ? '+' : ''}
      </motion.p>
      <p className="text-[#A1A1AA] text-sm mt-2">{label}</p>
    </div>
  );
};

// Platform Card Component
const PlatformCard = ({ platform, language }) => {
  const IconComponent = platform.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -8 }}
      className="group relative"
    >
      <div 
        className="absolute inset-0 rounded-3xl blur-xl opacity-0 group-hover:opacity-60 transition-opacity duration-500"
        style={{ backgroundColor: `${platform.color}30` }}
      />
      <div className="relative bg-white/[0.03] backdrop-blur-xl rounded-3xl p-8 border border-white/10 hover:border-[#d4af37]/30 transition-all duration-500 h-full overflow-hidden">
        {/* Gradient overlay on hover */}
        <div 
          className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500"
          style={{ background: `linear-gradient(135deg, ${platform.color}20, transparent)` }}
        />
        
        {/* Header */}
        <div className="relative flex items-center gap-4 mb-6">
          <motion.div 
            whileHover={{ rotate: 5, scale: 1.1 }}
            className="w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-300 border border-white/5 overflow-hidden"
            style={{ backgroundColor: `${platform.color}15` }}
          >
            {platform.logo ? (
              <img 
                src={platform.logo} 
                alt={platform.name} 
                className={`object-contain drop-shadow-lg ${
                  platform.id === 'cgm_coin' ? 'w-20 h-20 scale-150' :
                  platform.id === '606_community' ? 'w-14 h-14' :
                  platform.id === '606_mediadocs' ? 'w-14 h-14' :
                  'w-full h-full p-1'
                }`} 
              />
            ) : IconComponent ? (
              <IconComponent className="h-10 w-10" style={{ color: platform.color }} />
            ) : null}
          </motion.div>
          <div>
            <h3 className="text-xl font-bold text-white group-hover:text-[#d4af37] transition-colors" dir="ltr" style={{ textAlign: language === 'ar' ? 'right' : 'left' }}>
              {platform.name}
            </h3>
            <p className="text-sm font-medium" style={{ color: platform.color }}>
              {language === 'ar' ? platform.nameAr : platform.nameEn}
            </p>
          </div>
        </div>

        {/* Description */}
        <p className="relative text-[#A1A1AA] mb-6 leading-relaxed text-sm">
          {language === 'ar' ? platform.descAr : platform.descEn}
        </p>

        {/* Features */}
        <div className="relative space-y-3 mb-8">
          {platform.features.map((feature, i) => {
            const FeatureIcon = feature.icon;
            return (
              <div key={i} className="flex items-center gap-3 text-[#A1A1AA]">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${platform.color}10` }}>
                  <FeatureIcon className="h-4 w-4" style={{ color: platform.color }} />
                </div>
                <span className="text-sm">{language === 'ar' ? feature.textAr : feature.textEn}</span>
              </div>
            );
          })}
        </div>

        {/* Action */}
        <motion.a 
          href={platform.url}
          target="_blank"
          rel="noopener noreferrer"
          whileHover={{ scale: 1.03, boxShadow: `0 0 25px ${platform.color}40` }}
          whileTap={{ scale: 0.97 }}
          className="relative inline-flex items-center gap-2 px-6 py-3 text-white font-bold rounded-xl transition-all"
          style={{ backgroundColor: platform.color }}
        >
          {language === 'ar' ? 'زيارة المنصة' : 'Visit Platform'}
          <ExternalLink className="h-4 w-4" />
        </motion.a>
      </div>
    </motion.div>
  );
};

const Home = () => {
  const { language } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // All platforms data
  const platforms = [
    {
      id: 'cloudgm',
      name: 'CLOUDGM',
      nameAr: 'منصة العملاء',
      nameEn: 'Customer Platform',
      descAr: 'منصة شاملة للعملاء توفر خدمات العقارات، الأرقام المميزة، قطع غيار السيارات، والمتجر الإلكتروني.',
      descEn: 'Comprehensive customer platform providing real estate, premium numbers, car parts, and e-commerce services.',
      logo: '/brand/ast-logo-cloudgm-market-white.png',
      url: 'https://www.cloudgm.com',
      color: '#1E9BD9',
      features: [
        { icon: Building2, textAr: 'عقارات للبيع والإيجار', textEn: 'Real Estate Sales & Rent' },
        { icon: Store, textAr: 'متجر إلكتروني متكامل', textEn: 'Complete E-commerce' },
      ]
    },
    {
      id: 'cloudgm_business',
      name: 'CLOUDGM BUSINESS',
      nameAr: 'منصة التجار',
      nameEn: 'Merchant Platform',
      descAr: 'منصة متخصصة للتجار تمكنهم من إنشاء متاجرهم الإلكترونية والوصول لقاعدة عملاء واسعة.',
      descEn: 'Specialized platform enabling merchants to create online stores and reach a wide customer base.',
      logo: '/brand/ast-logo-cloudgm-business-white.png',
      url: 'https://www.cloudgm.business',
      color: '#8B5CF6',
      features: [
        { icon: Store, textAr: 'إنشاء متاجر متعددة', textEn: 'Create Multiple Stores' },
        { icon: Users, textAr: 'إدارة المنتجات والطلبات', textEn: 'Manage Products & Orders' },
      ]
    },
    {
      id: 'cgm_coin',
      name: 'CGM COIN',
      nameAr: 'العملة الرقمية',
      nameEn: 'Digital · Decentralized · P2P',
      descAr: 'عملة رقمية لا مركزية من نظير إلى نظير، مصممة لتشغيل نظام دفع ومكافآت مستقبلي عبر منظومة CloudGM.',
      descEn: 'A digital, decentralized, peer-to-peer currency engineered as the native unit of value across the CloudGM ecosystem.',
      logo: '/brand/ast-logo-cgm-coin.png',
      url: 'https://cloudgmcoin.com',
      color: '#d4af37',
      features: [
        { icon: Coins, textAr: 'مدفوعات ومكافآت رقمية', textEn: 'Digital Payments & Rewards' },
        { icon: Shield, textAr: 'آمنة وشفافة', textEn: 'Secure & Transparent' },
      ]
    },
    {
      id: '606_fitness',
      name: '606 FITNESS',
      nameAr: 'اللياقة البدنية',
      nameEn: 'Fitness Platform',
      descAr: 'منصة متكاملة للياقة البدنية - متابعة التدريبات والنظام الغذائي مع متجر متخصص ومدربين محترفين.',
      descEn: 'Complete fitness platform - training and nutrition tracking with specialized store and professional trainers.',
      icon: Dumbbell,
      url: 'https://606fitness.com',
      color: '#10b981',
      features: [
        { icon: Dumbbell, textAr: 'برامج تدريب متخصصة', textEn: 'Specialized Training Programs' },
        { icon: Users, textAr: 'مدربين محترفين', textEn: 'Professional Trainers' },
      ]
    },
    {
      id: '606_community',
      name: '606 COMMUNITY',
      nameAr: 'بوت ديسكورد للألعاب',
      nameEn: 'Discord Gaming HQ',
      descAr: 'بوت ديسكورد متكامل مع موسيقى، إشراف، ومساعد ذكي Gemini 3 متعدد اللغات - يُتحكم من لوحة تحكم واحدة.',
      descEn: 'Complete Discord bot with music, moderation, and Gemini 3 AI sidekick — controlled from a single esports-grade dashboard.',
      logo: '/brand/ast-logo-606-community.png',
      url: 'https://606community.com',
      color: '#FF6B00',
      features: [
        { icon: Gamepad2, textAr: 'موسيقى وإدارة القنوات', textEn: 'Music & Channel Control' },
        { icon: Users, textAr: 'مساعد Gemini 3 AI', textEn: 'Gemini 3 AI Chat' },
      ]
    },
    {
      id: '606_mediadocs',
      name: '606 MEDIADOCS',
      nameAr: 'مكتبة وسائط مجانية',
      nameEn: 'Free Media Library',
      descAr: 'آلاف القوالب والمستندات والصور والفيديوهات المجانية للمبدعين - استخدام تجاري مجاني بالكامل.',
      descEn: 'Thousands of free templates, documents, images and videos for creators — 100% free commercial use.',
      logo: '/brand/ast-logo-606-mediadocs.png',
      url: 'https://606mediadocs.com',
      color: '#8B7355',
      features: [
        { icon: FileText, textAr: 'قوالب PowerPoint و Word', textEn: 'PowerPoint & Word Templates' },
        { icon: Shield, textAr: 'استخدام تجاري مجاني', textEn: 'Free Commercial Use' },
      ]
    },
  ];
  
  return (
    <div className="min-h-screen bg-[#030303] overflow-x-hidden" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-[#d4af37]/3 rounded-full blur-[120px]" />
        <div className="absolute top-1/2 left-0 w-[400px] h-[400px] bg-[#1E9BD9]/3 rounded-full blur-[100px]" />
      </div>
      
      <FloatingParticles />

      {/* NEW Navigation Header */}
      <motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 w-full z-50 transition-all duration-500 ${
          scrolled 
            ? 'bg-[#030303]/90 backdrop-blur-2xl border-b border-white/10 shadow-[0_10px_40px_rgba(0,0,0,0.5)]' 
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex justify-between items-center">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center gap-3"
          >
            <img 
              src={TRIANGLE_ONLY}
              alt="ASB606 Logo"
              className="h-14 w-auto drop-shadow-[0_0_15px_rgba(212,175,55,0.4)]"
            />
            <div className="flex flex-col">
              <span className="text-white font-bold text-2xl tracking-wider leading-none">ASB606</span>
              <span className="text-[#d4af37] text-[10px] tracking-[0.25em] font-medium mt-0.5">PLATFORMS GROUP</span>
            </div>
          </motion.div>
          
          <div className="hidden md:flex items-center gap-8">
            <a href="#about" className="text-[#A1A1AA] hover:text-[#d4af37] transition-colors text-sm font-medium">
              {language === 'ar' ? 'من نحن' : 'About Us'}
            </a>
            <a href="#platforms" className="text-[#A1A1AA] hover:text-[#d4af37] transition-colors text-sm font-medium">
              {language === 'ar' ? 'منصاتنا' : 'Our Platforms'}
            </a>
            <a href="#contact" className="text-[#A1A1AA] hover:text-[#d4af37] transition-colors text-sm font-medium">
              {language === 'ar' ? 'تواصل معنا' : 'Contact Us'}
            </a>
          </div>
          
          <div className="flex items-center gap-4">
            <LanguageToggle />
            {/* زر لوحة التحكم مخفي - الدخول فقط عبر الرابط المشفر */}
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#d4af37]/10 rounded-full border border-[#d4af37]/30 mb-8"
          >
            <Sparkles className="h-4 w-4 text-[#d4af37]" />
            <span className="text-sm text-[#d4af37] font-medium">
              {language === 'ar' ? 'مجموعة منصات رائدة في الإمارات' : 'Leading Platforms Group in UAE'}
            </span>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-6 tracking-tight leading-tight"
          >
            ASB606{' '}
            <span className="bg-gradient-to-r from-[#d4af37] via-[#f3e5ab] to-[#c9a227] text-transparent bg-clip-text">
              PLATFORMS
            </span>
            <br />
            <span className="text-[#A1A1AA] text-4xl md:text-5xl lg:text-6xl">GROUP</span>
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-lg md:text-xl text-[#A1A1AA] max-w-3xl mx-auto leading-relaxed mb-10"
          >
            {language === 'ar' 
              ? 'مجموعة منصات رائدة في مجال التقنية والحلول الرقمية في دولة الإمارات العربية المتحدة. نقدم منصات متكاملة للتجارة الإلكترونية والخدمات الحكومية.'
              : 'A leading platforms group in technology and digital solutions in the UAE. We provide integrated platforms for e-commerce and government services.'}
          </motion.p>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="flex flex-col sm:flex-row justify-center gap-4"
          >
            <motion.a 
              href="#platforms"
              whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(212,175,55,0.4)" }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-gradient-to-r from-[#d4af37] to-[#c9a227] text-black font-bold rounded-xl flex items-center justify-center gap-2 transition-all"
            >
              {language === 'ar' ? 'استكشف منصاتنا' : 'Explore Platforms'}
              <ArrowRight className="h-5 w-5" />
            </motion.a>
            <motion.a 
              href="#about"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 border border-white/20 text-white rounded-xl hover:bg-white/5 flex items-center justify-center gap-2 transition-all"
            >
              <Play className="h-5 w-5" />
              {language === 'ar' ? 'تعرف علينا' : 'About Us'}
            </motion.a>
          </motion.div>
          
          {/* Scroll Indicator */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, y: [0, 10, 0] }}
            transition={{ delay: 1.2, y: { repeat: Infinity, duration: 2 } }}
            className="absolute bottom-10 left-1/2 -translate-x-1/2"
          >
            <ChevronDown className="h-8 w-8 text-[#d4af37]/50" />
          </motion.div>
        </div>
        
        {/* Hero Background Image - Triangle Only */}
        <div className="absolute inset-0 flex items-center justify-center opacity-10">
          <img 
            src={TRIANGLE_ONLY}
            alt=""
            className="w-[600px] h-[600px] object-contain"
          />
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 relative">
        <div className="max-w-5xl mx-auto">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-4 gap-8 bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-8"
          >
            <StatCounter value="5+" label={language === 'ar' ? 'منصات رقمية' : 'Digital Platforms'} delay={0} />
            <StatCounter value="5000+" label={language === 'ar' ? 'مستخدم نشط' : 'Active Users'} delay={200} />
            <StatCounter value="100+" label={language === 'ar' ? 'تاجر شريك' : 'Partner Merchants'} delay={400} />
            <StatCounter value="24" label={language === 'ar' ? 'ساعة دعم' : 'Hour Support'} delay={600} />
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <span className="text-[#d4af37] text-sm font-semibold tracking-wider uppercase">
              {language === 'ar' ? 'عن المنصات' : 'About Us'}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
              {language === 'ar' ? 'من نحن' : 'Who We Are'}
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-transparent via-[#d4af37] to-transparent mx-auto"></div>
          </motion.div>
          
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
                {language === 'ar' ? 'مجموعة ASB606 للمنصات والتقنية' : 'ASB606 Platforms & Technology Group'}
              </h3>
              <p className="text-[#A1A1AA] leading-relaxed mb-6">
                {language === 'ar' 
                  ? 'نحن مجموعة منصات إماراتية متخصصة في تطوير الحلول الرقمية والمنصات الإلكترونية. تأسست مجموعتنا بهدف تقديم خدمات متكاملة تلبي احتياجات السوق الإماراتي والخليجي.'
                  : 'We are an Emirati platforms group specialized in developing digital solutions and electronic platforms. Our group was founded to provide integrated services that meet the needs of the UAE and Gulf market.'}
              </p>
              <p className="text-[#A1A1AA] leading-relaxed mb-8">
                {language === 'ar'
                  ? 'نؤمن بأن التقنية هي المحرك الأساسي للنمو والتطور، لذلك نسعى دائماً لتقديم أحدث الحلول التقنية.'
                  : 'We believe that technology is the main driver of growth and development, so we always strive to provide the latest technical solutions.'}
              </p>
              
              {/* Feature List */}
              <div className="space-y-4">
                {[
                  { icon: Shield, text: language === 'ar' ? 'أعلى معايير الأمان والحماية' : 'Highest Security Standards' },
                  { icon: Globe, text: language === 'ar' ? 'خدمات تغطي الإمارات والخليج' : 'Services covering UAE & Gulf' },
                  { icon: Users, text: language === 'ar' ? 'فريق متخصص من الخبراء' : 'Expert specialized team' },
                ].map((item, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-center gap-4"
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#d4af37]/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="h-5 w-5 text-[#d4af37]" />
                    </div>
                    <span className="text-white">{item.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#d4af37]/10 via-transparent to-[#d4af37]/10 rounded-3xl blur-xl"></div>
              <motion.div 
                whileHover={{ scale: 1.02, borderColor: 'rgba(212,175,55,0.6)' }}
                transition={{ duration: 0.3 }}
                className="relative bg-[#0a0a0a] border border-[#d4af37]/30 rounded-2xl p-12 flex flex-col items-center justify-center cursor-pointer"
              >
                {/* Triangle Logo - Larger and Interactive */}
                <motion.img 
                  src={LOGO_TRIANGLE}
                  alt="ASB606 PLATFORMS GROUP"
                  className="w-72 h-72 object-contain mb-8"
                  whileHover={{ scale: 1.1, filter: 'drop-shadow(0 0 20px rgba(212,175,55,0.5))' }}
                  transition={{ duration: 0.3 }}
                />
                <div className="text-center">
                  <p className="text-[#d4af37] font-bold text-xl tracking-wider mb-2">ASB606 PLATFORMS GROUP</p>
                  <p className="text-[#71717A] text-sm">
                    {language === 'ar' ? 'الإمارات العربية المتحدة' : 'United Arab Emirates'}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Platforms Section */}
      <section id="platforms" className="py-24 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <span className="text-[#d4af37] text-sm font-semibold tracking-wider uppercase">
              {language === 'ar' ? 'خدماتنا' : 'Our Services'}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
              {language === 'ar' ? 'منصاتنا الرقمية' : 'Our Digital Platforms'}
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-transparent via-[#d4af37] to-transparent mx-auto mb-6"></div>
            <p className="text-[#A1A1AA] max-w-2xl mx-auto">
              {language === 'ar' 
                ? 'نقدم مجموعة متكاملة من المنصات الرقمية المصممة لتلبية احتياجات العملاء والتجار'
                : 'We offer an integrated suite of digital platforms designed to meet the needs of customers and merchants'}
            </p>
          </motion.div>

          {/* Platforms Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {platforms.map((platform, index) => (
              <PlatformCard key={platform.id} platform={platform} language={language} />
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-6 relative">
        <div className="max-w-3xl mx-auto">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <span className="text-[#d4af37] text-sm font-semibold tracking-wider uppercase">
              {language === 'ar' ? 'اتصل بنا' : 'Get In Touch'}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
              {language === 'ar' ? 'تواصل معنا' : 'Contact Us'}
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-transparent via-[#d4af37] to-transparent mx-auto mb-6"></div>
            <p className="text-[#A1A1AA] max-w-2xl mx-auto">
              {language === 'ar' 
                ? 'يسعدنا تواصلك معنا. أرسل لنا رسالتك وسنرد عليك في أقرب وقت ممكن.'
                : 'We\'d love to hear from you. Send us a message and we\'ll respond as soon as possible.'}
            </p>
          </motion.div>

          {/* Contact Form Only */}
          <ContactForm language={language} />
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-4">
              <img 
                src={LOGO_FULL}
                alt="ASB606 Logo"
                className="h-16 w-auto opacity-90"
                style={{ imageRendering: 'auto' }}
              />
            </div>
            <p className="text-[#71717A] text-sm text-center">
              {language === 'ar' 
                ? '© 2026 ASB606 PLATFORMS GROUP. جميع الحقوق محفوظة.'
                : '© 2026 ASB606 PLATFORMS GROUP. All rights reserved.'}
            </p>
            <div className="flex items-center gap-6">
              <a href="https://www.cloudgm.com" target="_blank" rel="noopener noreferrer" className="text-[#71717A] hover:text-[#d4af37] text-sm transition-colors">CloudGM</a>
              <a href="https://www.cloudgm.business" target="_blank" rel="noopener noreferrer" className="text-[#71717A] hover:text-[#d4af37] text-sm transition-colors">CloudGM Business</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
